import { useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHelpCenterUrl, useIsIMWallet, useUpdaterChainId } from '~/hooks'
import { useIsSendByRelayer } from '~/components/Exchange/hooks'
import InteractiveMascot from '../../components/InteractiveMascot'
import { useAppDispatch } from '~/state/hooks'
import { useSpotSwapPair } from '~/state/dex/hooks'
import { updateSpotSwapUserTradeStatus } from '~/state/dex/actions'
import { SpotSwapUserTradeStatus } from '~/state/dex/type'
import { OrderState } from '@tokenlon/v6-sdk'
import { formatUnits } from 'viem'
import confetti from 'canvas-confetti'
import Button from '~/components/Button'
import { formatFormAmount } from '~/utils/numbers'
import { isMainnetOrSepolia } from '~/utils/chain'
import TokensChangeArea from './TokensChangeArea'
import {
  StyledOrderSuccessful,
  BackButton,
  Title,
  Content,
  StyledShield,
  StyledDescWrapper,
  StyledDesc,
  StyledLearnMore,
  StyledIconSuccess,
  StyledStatusLabel,
} from './styled'
import IconSwapArrow from '~/assets/images/swap-arrow.svg'
import IconGreenShield from '~/assets/images/greenShield.svg'
import IconSuccess from '~/assets/images/success-green-round.svg'

interface Props {
  orderState: OrderState
  isPMM: boolean
  handleBack: () => void
}

const OrderSuccessful: React.FC<Props> = ({
  orderState,
  isPMM,
  handleBack,
}) => {
  const { t } = useTranslation()
  const updaterChainId = useUpdaterChainId()
  const dispatch = useAppDispatch()
  const helpCenterUrl = useHelpCenterUrl('20588637587220')
  const [inputToken, outputToken] = useSpotSwapPair()
  const isIMWallet = useIsIMWallet()
  const isSendByRelayer = useIsSendByRelayer()

  const { precision: inputPrecision, symbol: inputSymbol } = inputToken
  const { precision: outputPrecision, symbol: outputSymbol } = outputToken

  const inputAmount = useMemo(() => {
    const { amountIn } = orderState
    return formatFormAmount(
      formatUnits(BigInt(amountIn), inputToken.decimals),
      inputPrecision,
    )
  }, [orderState, inputToken])

  const outputAmount = useMemo(() => {
    const { settleAmount } = orderState
    return formatFormAmount(
      formatUnits(BigInt(settleAmount), outputToken.decimals),
      outputPrecision,
    )
  }, [orderState, outputToken])

  const showShield =
    (isSendByRelayer || isPMM || isIMWallet) &&
    isMainnetOrSepolia(updaterChainId)

  useEffect(() => {
    const duration = 5 * 1000
    const animationEnd = Date.now() + duration
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 }

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min
    }

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now()

      if (timeLeft <= 0) {
        return clearInterval(interval)
      }

      const particleCount = 50 * (timeLeft / duration)
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      })
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      })
    }, 250)

    const timer = setTimeout(() => {
      dispatch(updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.NORMAL))
    }, duration)

    return () => {
      clearInterval(interval)
      clearTimeout(timer)
    }
  }, [])

  return (
    <StyledOrderSuccessful>
      <InteractiveMascot />
      <BackButton>
        <img src={IconSwapArrow} onClick={handleBack} />
      </BackButton>
      <Title>{t('preview_order')}</Title>
      <Content>
        {showShield ? (
          <StyledShield src={IconGreenShield} alt="greenShield" />
        ) : (
          <StyledIconSuccess src={IconSuccess} alt="success" />
        )}
        <StyledStatusLabel>
          {t('spotswap_order_success_page_status_label')}
        </StyledStatusLabel>
        <TokensChangeArea
          inputTokenAmount={inputAmount}
          outputTokenAmount={outputAmount}
        />
        {showShield && (
          <StyledDescWrapper direction="column" gap={8}>
            <StyledDesc>{t('spotswap_successful_modal_desc')}</StyledDesc>
            <StyledLearnMore href={helpCenterUrl} target="_blank">{`${t(
              'learn_more',
            )} >`}</StyledLearnMore>
          </StyledDescWrapper>
        )}
        <Button
          text={t('back_to_swap')}
          style={{ marginTop: 16 }}
          handleClick={handleBack}
        />
      </Content>
    </StyledOrderSuccessful>
  )
}

export default OrderSuccessful
